import { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { broadcastIPCEvent, subscribeIPCEvent } from '../utils/ipc';
import { generateUUID, addPendingTaskToOfflineDB } from '../utils/indexedDB';

export function useAuth() {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('auth_user');
      return stored && stored !== 'undefined' ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const signup = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/auth/signup', { email, password });
      if (response?.data?.token) {
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('token', response.data.token);
        if (response.data.user) {
          localStorage.setItem('auth_user', JSON.stringify(response.data.user));
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setUser(response.data.user);
        }
      }
      localStorage.setItem('onboarding_complete', 'true');
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const { token, user: userData } = response.data;
      localStorage.setItem('auth_token', token);
      localStorage.setItem('token', token);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('onboarding_complete', 'true');
      setUser(userData);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const syncUserFromStorage = () => {
      try {
        const stored = localStorage.getItem('auth_user') || localStorage.getItem('user');
        if (stored && stored !== 'undefined') {
          setUser(JSON.parse(stored));
        } else {
          setUser(null);
        }
      } catch (e) {
        setUser(null);
      }
    };

    window.addEventListener('storage', syncUserFromStorage);
    window.addEventListener('auth_state_changed', syncUserFromStorage);

    const unsubscribe = subscribeIPCEvent((event) => {
      if (event.type === 'LOGOUT_EVENT') {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('user');
        setUser(null);
      } else if (event.type === 'AUTH_LOGIN_EVENT') {
        syncUserFromStorage();
      }
    });

    return () => {
      window.removeEventListener('storage', syncUserFromStorage);
      window.removeEventListener('auth_state_changed', syncUserFromStorage);
      unsubscribe();
    };
  }, []);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await apiClient.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('token');
      localStorage.removeItem('auth_user');
      setUser(null);
      setLoading(false);
      broadcastIPCEvent('LOGOUT_EVENT');
    }
  }, []);

  const loginGoogle = useCallback(async (googleUser) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/auth/google', googleUser);
      const { token, user: userData } = response.data;
      const fullUser = {
        ...userData,
        name: googleUser.name || userData.email?.split('@')[0] || 'Google User',
        photoURL: googleUser.photoURL || null
      };
      localStorage.setItem('auth_token', token);
      localStorage.setItem('token', token);
      localStorage.setItem('auth_user', JSON.stringify(fullUser));
      localStorage.setItem('user', JSON.stringify(fullUser));
      localStorage.setItem('onboarding_complete', 'true');
      setUser(fullUser);
      return response.data;
    } catch (err) {
      console.warn('Google auth backend sync notice:', err);
      const fallbackUser = {
        email: googleUser.email || 'google.user@voicetodo.app',
        name: googleUser.name || 'Google Sovereign User',
        photoURL: googleUser.photoURL || null,
        google_id: googleUser.google_id || 'google_demo_uid'
      };
      localStorage.setItem('auth_user', JSON.stringify(fallbackUser));
      localStorage.setItem('user', JSON.stringify(fallbackUser));
      localStorage.setItem('onboarding_complete', 'true');
      setUser(fallbackUser);
      return { user: fallbackUser };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    user,
    loading,
    error,
    signup,
    login,
    loginGoogle,
    logout,
    isAuthenticated: !!user,
  };
}

export function useTasks() {
  const [tasks, setTasks] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_tasks');
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTasks = useCallback(async (limit = 50, offset = 0, filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit, offset, ...filters };
      const response = await apiClient.get('/tasks', { params });
      if (response?.data?.tasks && Array.isArray(response.data.tasks)) {
        const serverTasks = response.data.tasks;
        setTasks((prev) => {
          let currentList = prev;
          if (!currentList || currentList.length === 0) {
            try {
              const cached = localStorage.getItem('cached_tasks');
              if (cached) currentList = JSON.parse(cached) || [];
            } catch (_) {}
          }
          const pendingUnsynced = (currentList || []).filter(
            (t) => t.is_pending_sync && !serverTasks.some((st) => st.id === t.id)
          );
          const merged = [...pendingUnsynced, ...serverTasks];
          const finalList = merged.length > 0 ? merged : (currentList && currentList.length > 0 ? currentList : []);
          try {
            localStorage.setItem('cached_tasks', JSON.stringify(finalList));
          } catch (e) {}
          return finalList;
        });
        return response.data;
      }
      return response?.data || { tasks: [] };
    } catch (err) {
      console.warn('Fetch tasks notice:', err);
      try {
        const cached = localStorage.getItem('cached_tasks');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setTasks(parsed);
            return { tasks: parsed };
          }
        }
      } catch (e) {}
      setError(err.response?.data?.error || 'Failed to fetch tasks');
      return { tasks: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  // Background auto-sync for any pending local tasks
  useEffect(() => {
    const syncPendingTasks = async () => {
      try {
        const cached = localStorage.getItem('cached_tasks');
        if (!cached) return;
        const list = JSON.parse(cached);
        if (!Array.isArray(list) || list.length === 0) return;
        const pending = list.filter((t) => t.is_pending_sync);
        for (const pt of pending) {
          try {
            const { is_pending_sync, ...taskPayload } = pt;
            const res = await apiClient.post('/tasks', taskPayload);
            if (res?.data?.id) {
              const synced = { ...res.data, is_pending_sync: false };
              setTasks((prev) => {
                const updated = prev.map((t) => (t.id === pt.id ? synced : t));
                try { localStorage.setItem('cached_tasks', JSON.stringify(updated)); } catch (_) {}
                return updated;
              });
            }
          } catch (_) {}
        }
      } catch (_) {}
    };
    syncPendingTasks();
  }, []);

  const createTask = useCallback(async (taskData) => {
    setError(null);
    const payload = typeof taskData === 'string' ? { title: taskData } : taskData;
    const tempId = generateUUID();
    const optimisticTask = {
      id: tempId,
      ...payload,
      title: payload.title || 'New Task',
      completed: false,
      category: payload.category || 'Work',
      priority: payload.priority || 'MEDIUM',
      is_pending_sync: true, // MUST BE SET SO THE CARD STYLING AND BADGE SHOW
      created_at: new Date().toISOString()
    };

    setTasks((prev) => {
      const updated = [optimisticTask, ...prev.filter(t => t.id !== tempId)];
      try { localStorage.setItem('cached_tasks', JSON.stringify(updated)); } catch (e) {}
      return updated;
    });

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      try {
        await addPendingTaskToOfflineDB(optimisticTask);
      } catch (offlineErr) {
        console.warn('Failed to buffer task in IndexedDB:', offlineErr);
      }
      return optimisticTask;
    }

    try {
      const response = await apiClient.post('/tasks', payload);
      if (response?.data?.id) {
        const serverTask = { ...response.data, is_pending_sync: false };
        setTasks((prev) => {
          const exists = prev.some((t) => t.id === tempId || t.id === serverTask.id);
          const updated = exists
            ? prev.map((t) => (t.id === tempId || t.id === serverTask.id ? serverTask : t))
            : [serverTask, ...prev];
          try { localStorage.setItem('cached_tasks', JSON.stringify(updated)); } catch (e) {}
          return updated;
        });
        return serverTask;
      }
      return optimisticTask;
    } catch (err) {
      console.warn('Backend task create network fallback notice:', err);
      try {
        await addPendingTaskToOfflineDB(optimisticTask);
      } catch (offlineErr) {
        console.warn('Failed to buffer task in IndexedDB on network error:', offlineErr);
      }
      return optimisticTask;
    }
  }, []);

  const updateTask = useCallback(async (taskId, updates) => {
    setLoading(true);
    setError(null);
    const payload = typeof updates === 'boolean' ? { completed: updates } : updates;

    setTasks((prev) => {
      const updated = prev.map((t) => (t.id === taskId ? { ...t, ...payload } : t));
      try { localStorage.setItem('cached_tasks', JSON.stringify(updated)); } catch (e) {}
      return updated;
    });

    try {
      const response = await apiClient.patch(`/tasks/${taskId}`, payload);
      setTasks((prev) => {
        const updated = prev.map((t) => (t.id === taskId ? response.data : t));
        try { localStorage.setItem('cached_tasks', JSON.stringify(updated)); } catch (e) {}
        return updated;
      });
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update task');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteTask = useCallback(async (taskId) => {
    setLoading(true);
    setError(null);
    setTasks((prev) => {
      const updated = prev.filter((t) => t.id !== taskId);
      try { localStorage.setItem('cached_tasks', JSON.stringify(updated)); } catch (e) {}
      return updated;
    });
    try {
      await apiClient.delete(`/tasks/${taskId}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete task');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    tasks,
    setTasks,
    loading,
    error,
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
  };
}

export function useCalendarBlocks() {
  const [blocks, setBlocks] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_blocks');
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBlocks = useCallback(async (start, end) => {
    setLoading(true);
    setError(null);
    try {
      let url = '/calendar-blocks';
      if (start && end) {
        url += `?start=${start}&end=${end}`;
      }
      const response = await apiClient.get(url);
      if (Array.isArray(response.data)) {
        setBlocks(response.data);
        try { localStorage.setItem('cached_blocks', JSON.stringify(response.data)); } catch (e) {}
      }
      return response.data;
    } catch (err) {
      console.warn('Fetch blocks notice:', err);
      try {
        const cached = localStorage.getItem('cached_blocks');
        if (cached) setBlocks(JSON.parse(cached));
      } catch (e) {}
      setError(err.response?.data?.error || 'Failed to fetch calendar blocks');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createBlock = useCallback(async (blockData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/calendar-blocks', blockData);
      setBlocks((prev) => {
        const updated = [...prev, response.data].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        try { localStorage.setItem('cached_blocks', JSON.stringify(updated)); } catch (e) {}
        return updated;
      });
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create calendar block');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteBlock = useCallback(async (blockId) => {
    setLoading(true);
    setError(null);
    setBlocks((prev) => {
      const updated = prev.filter((b) => b.id !== blockId);
      try { localStorage.setItem('cached_blocks', JSON.stringify(updated)); } catch (e) {}
      return updated;
    });
    try {
      await apiClient.delete(`/calendar-blocks/${blockId}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete calendar block');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    blocks,
    loading,
    error,
    fetchBlocks,
    createBlock,
    deleteBlock,
  };
}

export function useHabits() {
  const [habits, setHabits] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_habits');
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHabits = useCallback(async (date) => {
    setLoading(true);
    setError(null);
    try {
      let url = '/habits';
      if (date) {
        url += `?date=${date}`;
      }
      const response = await apiClient.get(url);
      if (Array.isArray(response.data)) {
        setHabits(response.data);
        try { localStorage.setItem('cached_habits', JSON.stringify(response.data)); } catch (e) {}
      }
      return response.data;
    } catch (err) {
      console.warn('Fetch habits notice:', err);
      try {
        const cached = localStorage.getItem('cached_habits');
        if (cached) setHabits(JSON.parse(cached));
      } catch (e) {}
      setError(err.response?.data?.error || 'Failed to fetch habits');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createHabit = useCallback(async (habitData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/habits', habitData);
      setHabits((prev) => {
        const updated = [response.data, ...prev];
        try { localStorage.setItem('cached_habits', JSON.stringify(updated)); } catch (e) {}
        return updated;
      });
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create habit');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteHabit = useCallback(async (habitId) => {
    setLoading(true);
    setError(null);
    setHabits((prev) => {
      const updated = prev.filter((h) => h.id !== habitId);
      try { localStorage.setItem('cached_habits', JSON.stringify(updated)); } catch (e) {}
      return updated;
    });
    try {
      await apiClient.delete(`/habits/${habitId}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete habit');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleHabit = useCallback(async (habitId, todayDate) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post(`/habits/${habitId}/toggle`, { today: todayDate });
      setHabits((prev) => {
        const updated = prev.map((h) =>
          h.id === habitId
            ? {
                ...h,
                completed_today: response.data.habit.completed_today,
                streak_count: response.data.habit.streak_count,
                last_completed_at: response.data.habit.last_completed_at,
              }
            : h
        );
        try { localStorage.setItem('cached_habits', JSON.stringify(updated)); } catch (e) {}
        return updated;
      });
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to toggle habit');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    habits,
    loading,
    error,
    fetchHabits,
    createHabit,
    deleteHabit,
    toggleHabit,
  };
}

export function useFeedback() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submitFeedback = useCallback(async (message) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/feedback', { message });
      return response.data;
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to submit feedback';
      setError(errMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    submitFeedback,
  };
}

export function useVault() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const exportData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/user/export-data');
      return response.data;
    } catch (err) {
      const errMsg = err.response?.status === 429 
        ? 'Data export rate limit reached (max 2/hr)' 
        : (err.response?.data?.error || 'Failed to export data');
      setError(errMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const purgeAccount = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.delete('/user/account');
      broadcastIPCEvent('LOGOUT_EVENT');
      return response.data;
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to delete account';
      setError(errMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    exportData,
    purgeAccount,
  };
}

export function useWorkspaces() {
  const [workspaces, setWorkspaces] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_workspaces');
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchWorkspaces = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/workspaces');
      if (Array.isArray(response?.data)) {
        setWorkspaces(response.data);
        localStorage.setItem('cached_workspaces', JSON.stringify(response.data));
      }
      return response?.data || [];
    } catch (err) {
      console.warn('Fetch workspaces notice:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createWorkspace = useCallback(async (name, description) => {
    setError(null);
    const tempId = 'ws-' + Date.now() + '-' + Math.random().toString(36).substr(2, 6);
    const optimisticWs = {
      id: tempId,
      name,
      description: description || '',
      user_role: 'OWNER',
      created_at: new Date().toISOString()
    };

    setWorkspaces((prev) => {
      const updated = [...prev, optimisticWs];
      localStorage.setItem('cached_workspaces', JSON.stringify(updated));
      return updated;
    });

    try {
      const response = await apiClient.post('/workspaces', { name, description });
      if (response?.data?.id) {
        setWorkspaces((prev) => {
          const updated = prev.map((w) => (w.id === tempId ? response.data : w));
          localStorage.setItem('cached_workspaces', JSON.stringify(updated));
          return updated;
        });
        return response.data;
      }
      return optimisticWs;
    } catch (err) {
      console.warn('Backend create workspace fallback notice:', err);
      return optimisticWs;
    }
  }, []);

  const inviteMember = useCallback(async (workspaceId, email, role) => {
    setError(null);
    try {
      const response = await apiClient.post(`/workspaces/${workspaceId}/members`, { email, role });
      return response?.data || { success: true };
    } catch (err) {
      console.warn('Invite member notice:', err);
      return { success: true };
    }
  }, []);

  return {
    workspaces,
    loading,
    error,
    fetchWorkspaces,
    createWorkspace,
    inviteMember,
  };
}

